/**
 * Filebase Service - IPFS Uploads via Server-Side API
 *
 * Uploads files to Filebase (IPFS) using server-side API endpoint.
 * This approach bypasses browser CORS restrictions and keeps credentials secure.
 *
 * The server-side API (/api/upload-ipfs.js) handles the actual S3 upload.
 */

class FilebaseService {
  constructor() {
    // Check for API key (client-side authentication)
    // Note: S3 credentials are server-side only and not checked here
    const apiKey = process.env.REACT_APP_API_KEY?.trim();

    console.log('🔧 [FilebaseService] Initializing...', {
      hasApiKey: !!apiKey,
      uploadMethod: 'server-side API with authentication'
    });

    if (!apiKey) {
      console.warn('⚠️ [FilebaseService] API key not configured - IPFS uploads will require authentication');
      console.warn('💡 [FilebaseService] Set REACT_APP_API_KEY in your environment');
    }

    // Store basic configuration (S3 credentials are handled server-side)
    this.endpoint = '/api/upload-ipfs';
    this.apiKey = apiKey;
    this.configured = true; // Always configured since server handles credentials

    console.log('✅ [FilebaseService] Initialized successfully', {
      hasApiKey: !!apiKey,
      endpoint: this.endpoint,
      uploadMethod: 'authenticated server-side API'
    });
  }

  /**
   * Upload file to Filebase (IPFS) via server-side API
   * Uses server-side endpoint to bypass browser CORS restrictions
   *
   * @param {File} file - File to upload
   * @param {Object} metadata - Optional metadata
   * @returns {Promise<Object>} Upload result with IPFS CID
   */
  async uploadFile(file, metadata = {}) {
    console.log('┏━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┓');
    console.log('┃  FILEBASE IPFS UPLOAD (SERVER-SIDE)    ┃');
    console.log('┗━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━┛');
    console.log('📊 [FilebaseService] Service Configuration:', {
      endpoint: this.endpoint,
      uploadMethod: 'authenticated server-side API',
      hasApiKey: !!this.apiKey
    });
    console.log('📄 [FilebaseService] File to upload:', {
      name: file.name,
      size: `${(file.size / 1024).toFixed(2)} KB`,
      type: file.type
    });
    console.log('📝 [FilebaseService] Metadata:', metadata);

    try {
      // Prepare FormData for server-side upload
      console.log('📦 [FilebaseService] Preparing FormData...');
      const startPrep = Date.now();
      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify(metadata));
      console.log(`✅ [FilebaseService] FormData prepared in ${Date.now() - startPrep}ms`);

      // Upload via server-side API
      console.log('🌐 [FilebaseService] Upload URL:', this.endpoint);
      console.log('📤 [FilebaseService] Starting server-side IPFS upload...');
      console.log('💡 [FilebaseService] Using server-side API to bypass browser CORS restrictions');

      const startUpload = Date.now();

      // Prepare headers with authentication
      const headers = {};
      if (this.apiKey) {
        headers['X-API-Key'] = this.apiKey;
        console.log('🔑 [FilebaseService] Using API key authentication');
      } else {
        console.warn('⚠️ [FilebaseService] No API key configured - server may reject request');
        console.warn('💡 [FilebaseService] Set REACT_APP_API_KEY in your environment');
      }

      const response = await fetch('/api/upload-ipfs', {
        method: 'POST',
        headers: headers,
        body: formData
      });

      const uploadDuration = Date.now() - startUpload;
      console.log(`📨 [FilebaseService] Response received in ${uploadDuration}ms`);
      console.log('📊 [FilebaseService] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [FilebaseService] Server returned error:', errorText);
        throw new Error(`Server upload failed: ${response.status} ${response.statusText}`);
      }

      console.log('✅ [FilebaseService] Response OK, parsing JSON...');
      const result = await response.json();

      console.log('📋 [FilebaseService] Response data:', result);
      console.log('✅✅✅ [FilebaseService] IPFS UPLOAD SUCCESSFUL! ✅✅✅');
      console.log('🔗 [FilebaseService] IPFS CID:', result.cid);
      console.log('🌐 [FilebaseService] Gateway URL:', result.gatewayUrl);

      return {
        success: true,
        cid: result.cid || result.ipfsHash,
        fileName: result.fileName,
        originalName: file.name,
        fileSize: result.size || file.size,
        contentType: result.contentType || file.type,
        url: result.gatewayUrl,
        gatewayUrl: result.gatewayUrl,
        filebaseUrl: result.filebaseUrl,
        bucket: this.bucket
      };

    } catch (error) {
      console.error('❌❌❌ [FilebaseService] UPLOAD FAILED ❌❌❌');
      console.error('❌ [FilebaseService] Error name:', error.name);
      console.error('❌ [FilebaseService] Error message:', error.message);

      if (error.message.includes('fetch')) {
        console.error('💡 [FilebaseService] Network error - check server-side API endpoint');
        console.error('💡 [FilebaseService] Ensure /api/upload-ipfs.js is deployed');
      }

      console.error('❌ [FilebaseService] Full error:', error);
      throw new Error(`Filebase upload failed: ${error.message}`);
    }
  }

  /**
   * Get IPFS gateway URL for a CID
   *
   * @param {string} cid - IPFS CID or filename
   * @returns {string} Gateway URL
   */
  getGatewayUrl(cid) {
    return `https://ipfs.filebase.io/ipfs/${cid}`;
  }

  /**
   * Delete file from Filebase
   * Note: IPFS is immutable, but you can remove from your bucket
   *
   * @param {string} fileName - File name/key
   * @returns {Promise<boolean>} Success status
   */
  async deleteFile(fileName) {
    try {
      const { DeleteObjectCommand } = await import('@aws-sdk/client-s3');
      const command = new DeleteObjectCommand({
        Bucket: this.bucket,
        Key: fileName
      });

      await this.client.send(command);
      console.log('[FilebaseService] File deleted:', fileName);
      return true;
    } catch (error) {
      console.error('[FilebaseService] Delete failed:', error);
      return false;
    }
  }
}

// Export singleton instance
const filebaseService = new FilebaseService();

export default filebaseService;
export { FilebaseService };
